/** A physical key press reaching the control that claimed it, on the same path a click takes. */

import { useEffect, useRef } from "react";

import type { ControlNode } from "./controlTree";

const EDITABLE_TAGS: ReadonlySet<string> = new Set(["INPUT", "TEXTAREA", "SELECT"]);

/** Whether this press belongs to whoever is typing rather than to a control. */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (EDITABLE_TAGS.has(target.tagName)) return true;
  return target.closest("[contenteditable]:not([contenteditable='false'])") !== null;
}

/** The control this key press reaches. Auto-repeat, a Ctrl/Alt/Meta chord and a press aimed at an
 * editable element reach nothing. Matched on `event.code`, never on `event.key`. */
export function claimantOf(
  event: KeyboardEvent,
  claims: ReadonlyMap<string, ControlNode>,
): ControlNode | undefined {
  if (event.repeat) return undefined;
  if (event.ctrlKey || event.altKey || event.metaKey) return undefined;
  if (isEditableTarget(event.target)) return undefined;
  return claims.get(event.code);
}

/** The short label for a code: `KeyA` reads `A`, `Digit1` reads `1`, everything else is itself. */
export function keyLabel(code: string): string {
  if (code.startsWith("Key")) return code.slice(3);
  if (code.startsWith("Digit")) return code.slice(5);
  return code;
}

/** Fire the claiming control on key down, once per physical press. A claimed key's default action
 * is prevented, a held key needs a release to fire again, and a window blur releases every key. */
export function useKeyClaims(
  claims: ReadonlyMap<string, ControlNode>,
  onPress: (node: ControlNode, timeStamp: number) => void,
): void {
  const held = useRef<Set<string>>(new Set<string>());
  const latest = useRef({ claims, onPress });
  latest.current = { claims, onPress };

  useEffect(() => {
    const released = held.current;

    const down = (event: KeyboardEvent): void => {
      const node = claimantOf(event, latest.current.claims);
      if (node === undefined) return;

      event.preventDefault();
      if (released.has(event.code)) return;
      released.add(event.code);
      latest.current.onPress(node, event.timeStamp);
    };

    const up = (event: KeyboardEvent): void => {
      released.delete(event.code);
    };

    const clear = (): void => {
      released.clear();
    };

    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", clear);

    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", clear);
      released.clear();
    };
  }, []);
}
